import React from "react";
import { NavLink, Outlet } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import {
  SquaresFour,
  Briefcase,
  Ruler,
  Calculator,
  FileText,
  ClockCounterClockwise,
  UsersThree,
  Receipt,
  CalendarBlank,
  ChartLineUp,
  ListBullets,
  Gear,
  SignOut,
  Triangle,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/app/dashboard", k: "nav.dashboard", icon: SquaresFour, tid: "nav-dashboard" },
  { to: "/app/projects", k: "nav.projects", icon: Briefcase, tid: "nav-projects" },
  { to: "/app/takeoff", k: "nav.takeoff", icon: Ruler, tid: "nav-takeoff" },
  { to: "/app/estimator", k: "nav.estimator", icon: Calculator, tid: "nav-estimator" },
  { to: "/app/proposals", k: "nav.proposals", icon: FileText, tid: "nav-proposals" },
  { to: "/app/catalog", k: "nav.catalog", icon: ListBullets, tid: "nav-catalog" },
  { to: "/app/employees", k: "nav.employees", icon: UsersThree, tid: "nav-employees" },
  { to: "/app/timesheet", k: "nav.timesheet", icon: ClockCounterClockwise, tid: "nav-timesheet" },
  { to: "/app/invoices", k: "nav.invoices", icon: Receipt, tid: "nav-invoices" },
  { to: "/app/calendar", k: "nav.calendar", icon: CalendarBlank, tid: "nav-calendar" },
  { to: "/app/accounting", k: "nav.accounting", icon: ChartLineUp, tid: "nav-accounting" },
  { to: "/app/settings", k: "nav.settings", icon: Gear, tid: "nav-settings" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useI18n();

  return (
    <div className="min-h-screen bg-white text-zinc-900 grid grid-cols-1 md:grid-cols-[240px_1fr]">
      <aside className="border-r border-zinc-200 bg-white flex flex-col">
        <div className="p-5 border-b border-zinc-200 flex items-center gap-3">
          <div className="w-7 h-7 bg-orange-600 flex items-center justify-center">
            <Triangle size={14} weight="fill" className="text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-black text-sm tracking-tight">C.H.C.</div>
            <div className="label-eyebrow text-[9px] text-zinc-500 truncate">LES ENTREPRISES</div>
          </div>
        </div>

        <div className="px-5 pt-3 pb-2 flex items-center justify-between">
          <p className="label-eyebrow text-zinc-400">// {t("nav.workspace")}</p>
          <div className="border border-zinc-300 inline-flex">
            {["en", "fr"].map((l) => (
              <button
                key={l}
                data-testid={`sidebar-lang-${l}`}
                onClick={() => setLang(l)}
                className={`text-[10px] font-bold tracking-wider px-1.5 py-0.5 transition-colors ${lang === l ? "bg-zinc-900 text-white" : "bg-white text-zinc-700 hover:bg-zinc-100"}`}
              >
                {l.toUpperCase()}
              </button>
            ))}
          </div>
        </div>

        <nav className="flex-1 pb-3">
          {NAV.map(({ to, k, icon: Icon, tid }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={tid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-5 py-2.5 text-sm font-medium border-l-2 transition-colors duration-150 ${
                  isActive
                    ? "border-orange-600 bg-zinc-50 text-zinc-900"
                    : "border-transparent text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900"
                }`
              }
            >
              <Icon size={18} weight="duotone" />
              {t(k)}
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-zinc-200 p-4">
          <div className="flex items-center gap-3 mb-3">
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-9 h-9 border border-zinc-300" />
            ) : (
              <div className="w-9 h-9 bg-zinc-200 flex items-center justify-center text-xs font-bold">
                {user?.name?.[0] ?? "U"}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-xs font-semibold truncate" data-testid="user-name">{user?.name}</div>
              <div className="text-[10px] text-zinc-500 truncate mono">{user?.email}</div>
            </div>
          </div>
          <Button
            data-testid="logout-button"
            variant="outline"
            size="sm"
            onClick={logout}
            className="w-full rounded-none border-zinc-300 hover:border-zinc-900 hover:bg-zinc-900 hover:text-white text-xs font-bold uppercase tracking-wider"
          >
            <SignOut size={14} className="mr-2" /> {t("common.signout")}
          </Button>
        </div>
      </aside>

      <main className="min-h-screen">
        <Outlet />
      </main>
    </div>
  );
}
